import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule, Optional, SkipSelf } from '@angular/core';
import { AIRCRAFT_LOGBOOK_CONFIG, AircraftLogbookConfig } from '@garmin-avcloud/avcloud-aircraft-logbook';
import { SCOPE_AUTHORIZATION } from '@garmin-avcloud/avcloud-fly-web-common/admin-authorization';
import { SKU_SERVICE_TOKEN, SUBSCRIPTIONS_JOB_MANAGEMENT_TOKEN } from '@garmin-avcloud/avcloud-fly-web-common/api';
import { AircraftModule } from '@garmin-avcloud/avcloud-fly-web-common/api/aircraft';
import { CustomerModule } from '@garmin-avcloud/avcloud-fly-web-common/api/customer';
import { DevicesModule } from '@garmin-avcloud/avcloud-fly-web-common/api/devices';
import { OrgsRolesModule } from '@garmin-avcloud/avcloud-fly-web-common/api/orgs-roles';
import { TelephonyDomainModule } from '@garmin-avcloud/avcloud-fly-web-common/api/telephony';
import { FLY_WEB_COMMON_CONFIG, FlyWebCommonConfig } from '@garmin-avcloud/avcloud-fly-web-common/config';
import { ORG_ACCESS_CONTROLS_TOKEN } from '@garmin-avcloud/avcloud-fly-web-common/models';
import { ORG_SETTINGS_SERVICE_TOKEN, OrgContextInterceptorService } from '@garmin-avcloud/avcloud-fly-web-common/orgs';
import { ADMIN_LEGACY_DEVICE_SERVICE_TOKEN } from '@garmin-avcloud/avcloud-fly-web-common/shared';
import { PROTOBUF_PARSER_SERVICE_CONFIG_TOKEN } from '@garmin-avcloud/avcloud-web-data-services/aircraft-logbook';
import { WEB_DATA_SERVICES_CONFIG } from '@garmin-avcloud/avcloud-web-data-services/config';
import {
  AIRCRAFT_SERVICE_CONFIG_TOKEN,
  AircraftServiceConfig,
  AuthService,
  CUSTOMER_SERVICE_CONFIG_TOKEN,
  CustomerServiceConfig,
  FEATURE_GUARD_CONFIG_TOKEN,
  // It is OK to import this in the CoreModule because we must instantiate it.
  // eslint-disable-next-line no-restricted-imports
  FeaturesService,
  LocalStorageService
} from '@garmin-avcloud/avcloud-web-utils';
import {
  LOGGING_DOMAIN_LOGGING_SERVICE_CONFIG,
  LoggingDomainLoggingService,
  LoggingDomainLoggingServiceConfig,
  provideLoggingDomainLogging
} from '@garmin-avcloud/avcloud-web-utils/logging/logging-domain';
import { environment } from '../../../environments/environment';
import { OrgSettingsService } from '../../features/orgs-roles/services/org-settings/org-settings.service';
import { AdminLegacyDeviceService } from '../../shared/services/admin-legacy-device.service';
import { SkuService } from '../../shared/services/sku.service';
import { SubscriptionJobManagementService } from '../../shared/services/subscriptions/subscription-job-management.service';
import { httpInterceptor } from '../interceptors/http-interceptor';
import { CustomersServiceWithCacheModule } from '../services/customers/customers-cache-service.module';
import { DeviceSummariesServiceWithCacheModule } from '../services/device-summaries-cache/devices-cache-service.module';
import { FeatureFlagService } from '../services/feature-flag/feature-flag.service';
import { LdapTokenService } from '../services/ldap-token/ldap-token.service';
import { OrgAccessControlsService } from '../services/org-access-controls/org-access-controls.service';
import { UserServiceModule } from '../services/user/user-service.module';

@NgModule({
  imports: [
    CommonModule,
    CustomersServiceWithCacheModule.forRoot(),
    DeviceSummariesServiceWithCacheModule.forRoot(),
    UserServiceModule.forRoot(),
    OrgsRolesModule,
    CustomerModule,
    AircraftModule,
    DevicesModule,
    TelephonyDomainModule
  ],
  providers: [
    LocalStorageService,
    {
      provide: AuthService,
      useExisting: LdapTokenService
    },
    {
      provide: SCOPE_AUTHORIZATION,
      useExisting: LdapTokenService
    },
    {
      provide: ORG_ACCESS_CONTROLS_TOKEN,
      useExisting: OrgAccessControlsService
    },
    {
      provide: AIRCRAFT_SERVICE_CONFIG_TOKEN,
      useValue: {
        environment: environment.name === 'LOCAL' ? 'TEST' : environment.name,
        clientId: environment.clientId
      } as AircraftServiceConfig
    },
    {
      provide: CUSTOMER_SERVICE_CONFIG_TOKEN,
      useValue: {
        clientId: environment.clientId,
        environment: environment.name === 'LOCAL' ? 'TEST' : environment.name
      } as CustomerServiceConfig
    },
    provideHttpClient(withInterceptors([httpInterceptor]), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrgContextInterceptorService,
      multi: true
    },
    {
      provide: SUBSCRIPTIONS_JOB_MANAGEMENT_TOKEN,
      useExisting: SubscriptionJobManagementService
    },
    {
      provide: SKU_SERVICE_TOKEN,
      useExisting: SkuService
    },
    {
      provide: FLY_WEB_COMMON_CONFIG,
      useValue: {
        regaliaIconsPath: 'assets/garmin-regalia-icons',
        environment: environment.name,
        site: 'FALA'
      } as FlyWebCommonConfig
    },
    {
      provide: WEB_DATA_SERVICES_CONFIG,
      useValue: {
        environment: environment.name === 'LOCAL' ? 'TEST' : environment.name
      }
    },
    // FALA's FeatureFlagService is provided here for the web-utils FeatureGuard to use
    {
      provide: FeaturesService,
      useExisting: FeatureFlagService
    },
    {
      provide: FEATURE_GUARD_CONFIG_TOKEN,
      useValue: {
        redirectRoute: 'not-found'
      }
    },
    {
      provide: SKU_SERVICE_TOKEN,
      useExisting: SkuService
    },
    {
      provide: ORG_SETTINGS_SERVICE_TOKEN,
      useExisting: OrgSettingsService
    },
    {
      provide: ADMIN_LEGACY_DEVICE_SERVICE_TOKEN,
      useExisting: AdminLegacyDeviceService
    },
    {
      provide: PROTOBUF_PARSER_SERVICE_CONFIG_TOKEN,
      useValue: {
        protoBundlePath: 'assets/bundle.json',
        webWorkerPath: 'assets/protobuf-parser.worker.js'
      }
    },
    {
      provide: AIRCRAFT_LOGBOOK_CONFIG,
      useValue: {
        clientId: environment.clientId,
        environment: environment.name === 'LOCAL' ? 'TEST' : environment.name,
        mapAssetsPath: 'assets/map',
        mapCapsUrl: environment.mapCapsLocation
      } as AircraftLogbookConfig
    },
    provideLoggingDomainLogging(),
    {
      provide: LOGGING_DOMAIN_LOGGING_SERVICE_CONFIG,
      useFactory: (): LoggingDomainLoggingServiceConfig => {
        let env: LoggingDomainLoggingServiceConfig['environment'] | null = null;

        switch (environment.name) {
          case 'DEV':
            env = 'develop';
            break;
          case 'LOCAL':
          case 'TEST':
            env = 'integration';
            break;
          case 'PROD':
            env = 'prod';
            break;
          case 'STAGE':
          default:
            env = null;
        }

        return {
          client: environment.clientId,
          environment: env as LoggingDomainLoggingServiceConfig['environment'],
          logIndex: environment.logging.logIndex,
          captureDebug: environment.logging.captureDebug,
          captureInfo: environment.logging.captureInfo,
          captureLog: environment.logging.captureLog,
          captureWarn: environment.logging.captureWarn,
          captureError: environment.logging.captureError
        };
      }
    },
    {
      provide: ErrorHandler,
      useExisting: LoggingDomainLoggingService
    }
  ]
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwErrorIfAlreadyLoaded(parentModule);
  }
}

const throwErrorIfAlreadyLoaded = (parentModule: any): void => {
  if (parentModule != null) {
    throw new Error('CoreModule has already been loaded. Import CoreModule in AppModule only.');
  }
};
